/**
 * Interactive Slider Component
 * Handles complex multi-column slider with animations, touch support, and expandable text
 */
;(function () {
	'use strict'

	/**
	 * Initialize a single interactive slider
	 * @param {HTMLElement} sliderContainer - The slider container element
	 */
	function initSlider(sliderContainer) {
		if (!sliderContainer) return

		const columns = sliderContainer.querySelectorAll('.ui-s-col')
		const container = sliderContainer.parentElement.parentElement.parentElement // Get to section container
		const nextBtn = container.querySelector('.ui-s-next')
		const prevBtn = container.querySelector('.ui-s-prev')
		const dots = container.querySelectorAll('.ui-s-dots button')

		let currentPage = 0
		let maxPages = 1
		let autoplayInterval = null
		let touchStartY = 0
		let touchEndY = 0

		// Calculate maximum pages based on slides in first column
		if (columns.length > 0) {
			const firstColumnSlides = columns[0].querySelectorAll('.ui-s-slide')
			maxPages = firstColumnSlides.length || 1
		}

		/**
		 * Updates the slider to show specified page
		 * @param {string} direction - Animation direction ('next' or 'prev')
		 */
		function updateSlider(direction = 'next') {
			columns.forEach((column, columnIndex) => {
				const slides = column.querySelectorAll('.ui-s-slide')
				const delay = columnIndex * 200 // Stagger animation

				setTimeout(() => {
					slides.forEach((slide, slideIndex) => {
						// Reset all transform classes
						slide.classList.remove(
							'opacity-0',
							'opacity-100',
							'translate-y-0',
							'translate-y-full',
							'-translate-y-full'
						)

						if (slideIndex === currentPage) {
							// Show current slide
							slide.style.zIndex = '10'
							slide.classList.add('opacity-100', 'translate-y-0')

							// Reset positioning after animation completes
							setTimeout(() => {
								slide.style.position = 'relative'
								slide.style.zIndex = ''
							}, 550)
						} else {
							// Hide other slides with appropriate animation
							slide.style.position = 'absolute'
							slide.style.top = '0'
							slide.style.left = '0'
							slide.style.width = '100%'

							if (direction === 'next') {
								slide.style.zIndex = slideIndex < currentPage ? '5' : '1'
								slide.classList.add('opacity-0', '-translate-y-full')
							} else {
								slide.style.zIndex = slideIndex > currentPage ? '5' : '1'
								slide.classList.add('opacity-0', 'translate-y-full')
							}
						}
					})
				}, delay)
			})

			updateNavigation()
		}

		/**
		 * Updates navigation button states and pagination dots
		 */
		function updateNavigation() {
			// Update previous button
			if (prevBtn) {
				if (currentPage === 0) {
					prevBtn.disabled = true
					prevBtn.classList.add('opacity-90')
				} else {
					prevBtn.disabled = false
					prevBtn.classList.remove('opacity-90')
				}
			}

			// Update next button
			if (nextBtn) {
				if (currentPage >= maxPages - 1) {
					nextBtn.disabled = true
					nextBtn.classList.add('opacity-90')
				} else {
					nextBtn.disabled = false
					nextBtn.classList.remove('opacity-90')
				}
			}

			// Update pagination dots
			dots.forEach((dot, index) => {
				if (index === currentPage) {
					dot.classList.remove('bg-gray-200')
					dot.classList.add('bg-gray-900')
				} else {
					dot.classList.remove('bg-gray-900')
					dot.classList.add('bg-gray-200')
				}
			})
		}

		/**
		 * Advances to next page
		 */
		function nextPage() {
			if (currentPage < maxPages - 1) {
				currentPage++
			} else {
				currentPage = 0 // Loop back to beginning
			}
			updateSlider('next')
		}

		/**
		 * Goes to previous page
		 */
		function prevPage() {
			if (currentPage > 0) {
				currentPage--
				updateSlider('prev')
			}
		}

		/**
		 * Starts autoplay functionality
		 */
		function startAutoplay() {
			stopAutoplay()
			autoplayInterval = setInterval(nextPage, 30000) // 30 second intervals
		}

		/**
		 * Stops autoplay
		 */
		function stopAutoplay() {
			if (autoplayInterval) {
				clearInterval(autoplayInterval)
				autoplayInterval = null
			}
		}

		/**
		 * Calculates and sets responsive image sizes
		 */
		function calculateSizes() {
			const images = sliderContainer.querySelectorAll('img[data-sizes="auto"]')
			images.forEach(img => {
				const container = img.closest('.ui-s-col')
				if (container) {
					const width = container.offsetWidth
					img.setAttribute('sizes', width + 'px')
				}
			})
		}

		/**
		 * Initializes expandable text functionality
		 */
		function initShowMoreLess() {
			const textAreas = sliderContainer.querySelectorAll('.tw-text')
			const CHAR_LIMIT = 150 // Character limit for text truncation

			textAreas.forEach((textArea, index) => {
				const textElement = textArea.querySelector('.tw-text-c p, .ui-w p, p')

				if (!textElement) return

				const fullText = textElement.innerHTML
				const textLength = textElement.textContent.trim().length

				if (textLength > CHAR_LIMIT) {
					const truncatedText = textElement.textContent.trim().substring(0, CHAR_LIMIT) + '...'
					const originalText = fullText

					// Set initial truncated text
					textElement.innerHTML = truncatedText

					// Create expand/collapse button
					const expandBtn = document.createElement('button')
					expandBtn.className =
						'mt-4 inline-flex items-center text-base font-medium text-gray-700 hover:text-black transition-colors'
					expandBtn.style.cssText =
						'margin-top: 16px; padding: 8px 16px; background: #f0f0f0; border: 2px solid #333; border-radius: 4px; cursor: pointer;'
					expandBtn.innerHTML = `
						<span class="tw-show-txt">Alle anzeigen</span>
						<span class="tw-hide-txt hidden">Weniger anzeigen</span>
						<svg class="ml-2 h-3 w-3 rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 12 12">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 2l4 4-4 4"/>
						</svg>
					`

					textElement.parentNode.appendChild(expandBtn)

					// Add toggle functionality
					let isExpanded = false
					expandBtn.addEventListener('click', function () {
						isExpanded = !isExpanded

						if (isExpanded) {
							textElement.innerHTML = originalText
							expandBtn.querySelector('.tw-show-txt').classList.add('hidden')
							expandBtn.querySelector('.tw-hide-txt').classList.remove('hidden')
							expandBtn.querySelector('svg').style.transform = 'rotate(-90deg)'
						} else {
							textElement.innerHTML = truncatedText
							expandBtn.querySelector('.tw-show-txt').classList.remove('hidden')
							expandBtn.querySelector('.tw-hide-txt').classList.add('hidden')
							expandBtn.querySelector('svg').style.transform = 'rotate(90deg)'
						}
					})
				}
			})
		}

		// Event Listeners

		// Next button
		if (nextBtn) {
			nextBtn.addEventListener('click', () => {
				nextPage()
				stopAutoplay()
				startAutoplay()
			})
		}

		// Previous button
		if (prevBtn) {
			prevBtn.addEventListener('click', () => {
				prevPage()
				stopAutoplay()
				startAutoplay()
			})
		}

		// Pagination dots
		dots.forEach((dot, index) => {
			dot.addEventListener('click', () => {
				const direction = index > currentPage ? 'next' : 'prev'
				currentPage = index
				updateSlider(direction)
				stopAutoplay()
				startAutoplay()
			})
		})

		// Mouse hover autoplay control
		sliderContainer.addEventListener('mouseenter', stopAutoplay)
		sliderContainer.addEventListener('mouseleave', startAutoplay)

		// Touch/swipe support
		sliderContainer.addEventListener(
			'touchstart',
			e => {
				touchStartY = e.touches[0].clientY
			},
			{ passive: true }
		)

		sliderContainer.addEventListener(
			'touchend',
			e => {
				touchEndY = e.changedTouches[0].clientY
				const swipeThreshold = 50

				if (touchStartY - touchEndY > swipeThreshold) {
					// Swipe up - next page
					nextPage()
					stopAutoplay()
					startAutoplay()
				} else if (touchEndY - touchStartY > swipeThreshold) {
					// Swipe down - previous page
					prevPage()
					stopAutoplay()
					startAutoplay()
				}
			},
			{ passive: true }
		)

		// Window resize handler
		window.addEventListener('resize', calculateSizes)

		// Initialize slider
		updateSlider()
		setTimeout(() => {
			calculateSizes()
			initShowMoreLess()
		}, 100)
		startAutoplay()
	}

	/**
	 * Initialize all interactive sliders on the page
	 */
	function initializeAllSliders() {
		const allSliders = document.querySelectorAll('[data-slider-id]')
		allSliders.forEach(slider => {
			initSlider(slider)
		})
	}

	// Auto-initialize when DOM is ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initializeAllSliders)
	} else {
		initializeAllSliders()
	}
})()
